<script>
/**
 * @component WhatsappConfig
 * @description ABM admin de la integración WhatsApp + LLM. Tres pestañas:
 *              1) Credenciales: API key + número virtual + webhook secret de 2chat
 *              2) Inteligencia Artificial: provider/model + 3 system prompts (vital/checkin/medication) + thresholds
 *              3) Mensajes: templates de mensajes que envía el bot, por tipo de tarea
 *
 *              Los secrets (API keys, webhookSecret) se muestran como `••••` si
 *              ya están guardados; solo se mandan al backend cuando el admin los
 *              edita explícitamente — el backend preserva el valor existente si
 *              recibe vacío.
 *
 * @example <WhatsappConfig />
 */
import whatsappConfigService from "@/services/whatsappConfig";

const TASK_TYPES = [
  { value: "vital", label: "Vital (mediciones)" },
  { value: "checkin", label: "Check-in (sí/no)" },
  { value: "medication", label: "Medicación" },
];

const TEMPLATE_FIELDS = [
  {
    key: "initialNotification",
    label: "Notificación inicial",
    desc: "Mensaje que dispara el bot al notificar la tarea.",
    placeholder:
      "Hola {patientFirstName}, es hora de medir tu {taskName}. Mandame el valor.",
  },
  {
    key: "confirmation",
    label: "Confirmación al completar",
    desc: "Cuando la tarea quedó completa con éxito.",
    placeholder:
      "✅ Registré tu {taskName}: {value}. ¡Gracias {patientFirstName}!",
  },
  {
    key: "clarification",
    label: "Aclaración (LLM no entendió)",
    desc: "Wrapper cuando el LLM pide aclaración. Usá {llmClarificationMessage} para incluir lo que pregunta el LLM.",
    placeholder: "{llmClarificationMessage}",
  },
  {
    key: "pendingReminder",
    label: "Recordatorio de tareas pendientes",
    desc: "Sufijo opcional cuando el paciente tiene otras tareas pendientes.",
    placeholder: "Te recuerdo que también tenés pendiente: {pendingTasksList}",
  },
  {
    key: "technicalError",
    label: "Error técnico",
    desc: "Cuando falla el LLM o hay timeout.",
    placeholder:
      "Recibimos tu mensaje pero tuvimos un problema técnico. Lo registramos para que un humano lo revise. Disculpá.",
  },
  {
    key: "imageInterpreted",
    label: "Confirmación desde imagen",
    desc: "Cuando se interpretó valor desde una foto (ej. tensiómetro).",
    placeholder:
      "✅ Registré {value} (interpretado de tu foto). ¡Gracias {patientFirstName}!",
  },
  {
    key: "imageUnclear",
    label: "Imagen no se pudo leer",
    desc: "Cuando la foto era borrosa o no se pudo extraer valor.",
    placeholder:
      "No pude leer bien el valor en tu foto. ¿Podrías mandar otra más clara, o escribir el valor a mano?",
  },
];

export default {
  name: "WhatsappConfig",

  data() {
    return {
      loading: false,
      saving: false,
      testing: false,
      activeTab: "credentials",
      activeTemplateType: "vital",
      config: null,

      // Form state — se hidrata desde this.config en mounted
      form: {
        credentials: {
          provider: "twochat",
          enabled: false,
          virtualNumber: "",
          sandboxMode: false,
          apiKey: "",
          webhookSecret: "",
          hasApiKey: false,
          hasWebhookSecret: false,
        },
        ai: {
          provider: "openai",
          model: "gpt-4o-mini",
          apiKey: "",
          hasApiKey: false,
          systemPrompts: { vital: "", checkin: "", medication: "" },
          thresholds: { autoComplete: 0.85, askClarification: 0.6 },
          maxClarificationRounds: 2,
        },
        messageTemplates: {
          vital: {},
          checkin: {},
          medication: {},
        },
      },

      // Estado del modal de prueba
      testDialog: {
        visible: false,
        toPhone: "",
        text: "",
        sending: false,
        result: null,
      },

      // Estado del sandbox del LLM
      llmTestDialog: {
        visible: false,
        taskType: "vital",
        taskName: "Presión arterial",
        expectedFormat: "sistólica/diastólica",
        patientMessage: "120/80",
        running: false,
        result: null,
      },

      taskTypes: TASK_TYPES,
      templateFields: TEMPLATE_FIELDS,
    };
  },

  computed: {
    /** @description Ítems del select de provider de LLM */
    llmProviders() {
      return [
        { value: "openai", label: "OpenAI" },
        { value: "claude", label: "Anthropic Claude" },
      ];
    },
  },

  async mounted() {
    await this.loadConfig();
  },

  methods: {
    /**
     * @description Carga la config actual desde el backend y la hidrata en el form.
     */
    async loadConfig() {
      this.loading = true;
      try {
        const { data } = await whatsappConfigService.getConfig();
        this.config = data;
        this.hydrateFormFromConfig(data);
      } catch (e) {
        this.$message.error("No se pudo cargar la configuración WhatsApp");
        this.$logger?.error?.(e);
      } finally {
        this.loading = false;
      }
    },

    /**
     * @description Mergea data del backend en el form local. Los campos secret
     *              (apiKey, webhookSecret) NO vienen — solo flags hasApiKey/hasWebhookSecret.
     * @param {Object} data
     */
    hydrateFormFromConfig(data) {
      const c = data?.credentials || {};
      const a = data?.ai || {};
      const t = data?.messageTemplates || {};

      this.form.credentials = {
        provider: c.provider || "twochat",
        enabled: !!c.enabled,
        virtualNumber: c.virtualNumber || "",
        sandboxMode: !!c.sandboxMode,
        apiKey: "",
        webhookSecret: "",
        hasApiKey: !!c.hasApiKey,
        hasWebhookSecret: !!c.hasWebhookSecret,
      };

      this.form.ai = {
        provider: a.provider || "openai",
        model: a.model || "gpt-4o-mini",
        apiKey: "",
        hasApiKey: !!a.hasApiKey,
        systemPrompts: {
          vital: a.systemPrompts?.vital || "",
          checkin: a.systemPrompts?.checkin || "",
          medication: a.systemPrompts?.medication || "",
        },
        thresholds: {
          autoComplete: a.thresholds?.autoComplete ?? 0.85,
          askClarification: a.thresholds?.askClarification ?? 0.6,
        },
        maxClarificationRounds: a.maxClarificationRounds ?? 2,
      };

      this.form.messageTemplates = {
        vital: { ...(t.vital || {}) },
        checkin: { ...(t.checkin || {}) },
        medication: { ...(t.medication || {}) },
      };
    },

    /**
     * @description Guarda toda la config. Los secrets vacíos se preservan
     *              en backend (no se pisan).
     */
    async save() {
      this.saving = true;
      try {
        const payload = {
          credentials: {
            provider: this.form.credentials.provider,
            enabled: this.form.credentials.enabled,
            virtualNumber: this.form.credentials.virtualNumber,
            sandboxMode: this.form.credentials.sandboxMode,
            apiKey: this.form.credentials.apiKey, // string vacío preserva existente
            webhookSecret: this.form.credentials.webhookSecret,
          },
          ai: {
            provider: this.form.ai.provider,
            model: this.form.ai.model,
            apiKey: this.form.ai.apiKey,
            systemPrompts: this.form.ai.systemPrompts,
            thresholds: this.form.ai.thresholds,
            maxClarificationRounds: this.form.ai.maxClarificationRounds,
          },
          messageTemplates: this.form.messageTemplates,
        };

        const { data } = await whatsappConfigService.saveConfig(payload);
        this.hydrateFormFromConfig(data);
        this.$message.success("Configuración guardada");
      } catch (e) {
        this.$message.error("Error al guardar la configuración");
      } finally {
        this.saving = false;
      }
    },

    /**
     * @description Abre el dialog para enviar un mensaje de prueba.
     */
    openTestDialog() {
      this.testDialog.visible = true;
      this.testDialog.result = null;
      this.testDialog.toPhone = "";
      this.testDialog.text = "Prueba de integración WhatsApp desde Quantux.";
    },

    async runTestMessage() {
      if (!this.testDialog.toPhone) {
        this.$message.warning("Indicá un teléfono destino");
        return;
      }
      this.testDialog.sending = true;
      this.testDialog.result = null;
      try {
        const { data } = await whatsappConfigService.testMessage({
          toPhone: this.testDialog.toPhone,
          text: this.testDialog.text,
        });
        this.testDialog.result = data;
        this.$message.success("Mensaje enviado");
      } catch (e) {
        this.testDialog.result = {
          error: e.response?.data?.message || e.message,
        };
        this.$message.error("Falló el envío");
      } finally {
        this.testDialog.sending = false;
      }
    },

    /**
     * @description Abre el sandbox del LLM.
     */
    openLlmTestDialog() {
      this.llmTestDialog.visible = true;
      this.llmTestDialog.result = null;
    },

    async runLlmTest() {
      this.llmTestDialog.running = true;
      this.llmTestDialog.result = null;
      try {
        const { data } = await whatsappConfigService.testLlm({
          taskType: this.llmTestDialog.taskType,
          taskName: this.llmTestDialog.taskName,
          expectedFormat: this.llmTestDialog.expectedFormat,
          patientMessage: this.llmTestDialog.patientMessage,
        });
        this.llmTestDialog.result = data;
      } catch (e) {
        this.llmTestDialog.result = {
          error: e.response?.data?.message || e.message,
        };
      } finally {
        this.llmTestDialog.running = false;
      }
    },
  },
};
</script>

<template lang="pug">
  .whatsapp-config(v-loading="loading")
    //- El título "WhatsApp + IA" ya lo pinta SettingsDashboard arriba. Solo
    //- dejamos un caption corto en una línea.
    p.whatsapp-config__caption
      | Integración con 2chat: el bot envía tareas al paciente y el LLM
      | interpreta sus respuestas para completarlas automáticamente.

    el-tabs(v-model="activeTab")
      //- ──────── PESTAÑA 1: CREDENCIALES ────────
      el-tab-pane(label="Credenciales" name="credentials")
        .whatsapp-config__panel
          .whatsapp-config__field
            el-checkbox(v-model="form.credentials.enabled")
              | Habilitar integración WhatsApp para esta organización

          .whatsapp-config__field
            label.whatsapp-config__label Provider
            el-select(v-model="form.credentials.provider" disabled)
              el-option(value="twochat" label="2chat")

          .whatsapp-config__field
            label.whatsapp-config__label Número virtual asignado
            el-input(
              v-model="form.credentials.virtualNumber"
              placeholder="+54 9 11 5555 1234"
            )
            .whatsapp-config__hint
              | Número que te dio 2chat. En formato E.164 con prefijo de país.

          .whatsapp-config__field
            label.whatsapp-config__label API Key de 2chat
            el-input(
              v-model="form.credentials.apiKey"
              type="password"
              show-password
              :placeholder="form.credentials.hasApiKey ? '•••• (ya guardada — dejá vacío para mantenerla)' : 'Pegá la API key'"
            )

          .whatsapp-config__field
            label.whatsapp-config__label Webhook Secret
            el-input(
              v-model="form.credentials.webhookSecret"
              type="password"
              show-password
              :placeholder="form.credentials.hasWebhookSecret ? '•••• (ya guardado — dejá vacío para mantenerlo)' : 'Secret HMAC para validar webhooks inbound'"
            )
            .whatsapp-config__hint
              | Configurá este mismo valor en el panel de 2chat como secret de
              | firma HMAC. Lo usamos para validar que los webhooks vienen
              | realmente de 2chat.

          .whatsapp-config__field
            el-checkbox(v-model="form.credentials.sandboxMode")
              | Modo sandbox (no envía mensajes reales, solo loguea — útil para testing)

          .whatsapp-config__actions
            el-button(type="primary" :loading="saving" @click="save") Guardar
            el-button(@click="openTestDialog" :disabled="!form.credentials.hasApiKey && !form.credentials.apiKey") Probar envío

      //- ──────── PESTAÑA 2: INTELIGENCIA ARTIFICIAL ────────
      el-tab-pane(label="Inteligencia Artificial" name="ai")
        .whatsapp-config__panel
          .whatsapp-config__row
            .whatsapp-config__field
              label.whatsapp-config__label Provider
              el-select(v-model="form.ai.provider")
                el-option(v-for="p in llmProviders" :key="p.value" :value="p.value" :label="p.label")
            .whatsapp-config__field
              label.whatsapp-config__label Modelo
              el-input(v-model="form.ai.model" placeholder="gpt-4o-mini")

          .whatsapp-config__field
            label.whatsapp-config__label API Key del LLM
            el-input(
              v-model="form.ai.apiKey"
              type="password"
              show-password
              :placeholder="form.ai.hasApiKey ? '•••• (ya guardada — dejá vacío para mantenerla)' : 'Pegá la API key'"
            )

          .whatsapp-config__divider System prompts por tipo de tarea
          .whatsapp-config__hint
            | Cada tipo de tarea (vital, checkin, medication) tiene reglas
            | distintas de interpretación, por eso cada uno tiene su prompt.
            | Si dejás un prompt vacío, se usa el default hardcodeado.

          .whatsapp-config__field
            label.whatsapp-config__label Vital (mediciones)
            el-input(
              v-model="form.ai.systemPrompts.vital"
              type="textarea"
              :rows="6"
              placeholder="Sos un parser de respuestas de pacientes a tareas de medición de signos vitales..."
            )

          .whatsapp-config__field
            label.whatsapp-config__label Checkin (sí/no)
            el-input(
              v-model="form.ai.systemPrompts.checkin"
              type="textarea"
              :rows="6"
              placeholder="Sos un parser de respuestas de pacientes a tareas de check-in..."
            )

          .whatsapp-config__field
            label.whatsapp-config__label Medication
            el-input(
              v-model="form.ai.systemPrompts.medication"
              type="textarea"
              :rows="6"
              placeholder="Sos un parser de respuestas de pacientes a tareas de toma de medicación..."
            )

          .whatsapp-config__divider Umbrales y reglas

          .whatsapp-config__row
            .whatsapp-config__field
              label.whatsapp-config__label Auto-completar si confidence ≥
              el-input-number(
                v-model="form.ai.thresholds.autoComplete"
                :min="0" :max="1" :step="0.05" :precision="2"
              )
            .whatsapp-config__field
              label.whatsapp-config__label Pedir aclaración si confidence ≥
              el-input-number(
                v-model="form.ai.thresholds.askClarification"
                :min="0" :max="1" :step="0.05" :precision="2"
              )
            .whatsapp-config__field
              label.whatsapp-config__label Máx. rondas de aclaración
              el-input-number(
                v-model="form.ai.maxClarificationRounds"
                :min="0" :max="5" :step="1"
              )

          .whatsapp-config__actions
            el-button(type="primary" :loading="saving" @click="save") Guardar
            el-button(@click="openLlmTestDialog" :disabled="!form.ai.hasApiKey && !form.ai.apiKey") Probar LLM

      //- ──────── PESTAÑA 3: MENSAJES (TEMPLATES) ────────
      el-tab-pane(label="Mensajes" name="messages")
        .whatsapp-config__panel
          .whatsapp-config__field
            label.whatsapp-config__label Tipo de tarea a editar
            el-radio-group(v-model="activeTemplateType")
              el-radio-button(v-for="t in taskTypes" :key="t.value" :label="t.value") {{ t.label }}

          .whatsapp-config__hint
            | Los templates soportan variables tipo
            | <code>{patientFirstName}</code>, <code>{taskName}</code>,
            | <code>{value}</code>, <code>{expectedFormat}</code>,
            | <code>{pendingTasksList}</code>, <code>{llmClarificationMessage}</code>.
            | Si dejás un template vacío, se usa el default hardcodeado.

          .whatsapp-config__field(v-for="field in templateFields" :key="field.key")
            label.whatsapp-config__label {{ field.label }}
            el-input(
              v-model="form.messageTemplates[activeTemplateType][field.key]"
              type="textarea"
              :rows="3"
              :placeholder="field.placeholder"
            )
            .whatsapp-config__hint {{ field.desc }}

          .whatsapp-config__actions
            el-button(type="primary" :loading="saving" @click="save") Guardar

    //- ──────── DIALOG: PROBAR ENVÍO ────────
    el-dialog(
      title="Enviar mensaje de prueba"
      :visible.sync="testDialog.visible"
      width="500px"
    )
      .whatsapp-config__field
        label.whatsapp-config__label Teléfono destino (E.164)
        el-input(v-model="testDialog.toPhone" placeholder="+54 9 11 5555 1234")
      .whatsapp-config__field
        label.whatsapp-config__label Texto
        el-input(v-model="testDialog.text" type="textarea" :rows="3")
      .whatsapp-config__test-result(v-if="testDialog.result")
        h4 Resultado
        pre {{ JSON.stringify(testDialog.result, null, 2) }}
      span(slot="footer")
        el-button(@click="testDialog.visible = false") Cerrar
        el-button(type="primary" :loading="testDialog.sending" @click="runTestMessage") Enviar

    //- ──────── DIALOG: SANDBOX LLM ────────
    el-dialog(
      title="Probar interpretación del LLM"
      :visible.sync="llmTestDialog.visible"
      width="600px"
    )
      .whatsapp-config__row
        .whatsapp-config__field
          label.whatsapp-config__label Tipo de tarea
          el-select(v-model="llmTestDialog.taskType")
            el-option(v-for="t in taskTypes" :key="t.value" :value="t.value" :label="t.label")
        .whatsapp-config__field
          label.whatsapp-config__label Nombre de la tarea
          el-input(v-model="llmTestDialog.taskName")
      .whatsapp-config__field
        label.whatsapp-config__label Formato esperado
        el-input(v-model="llmTestDialog.expectedFormat")
      .whatsapp-config__field
        label.whatsapp-config__label Mensaje del paciente
        el-input(v-model="llmTestDialog.patientMessage" type="textarea" :rows="3")
      .whatsapp-config__test-result(v-if="llmTestDialog.result")
        h4 Decisión del LLM
        pre {{ JSON.stringify(llmTestDialog.result, null, 2) }}
      span(slot="footer")
        el-button(@click="llmTestDialog.visible = false") Cerrar
        el-button(type="primary" :loading="llmTestDialog.running" @click="runLlmTest") Ejecutar
</template>

<style lang="scss" scoped>
.whatsapp-config {
  // Caption en una sola línea — el título lo pinta SettingsDashboard.
  &__caption {
    margin: 0 0 16px;
    color: #6b7280;
    font-size: 13px;
    line-height: 1.4;
  }

  &__panel {
    padding: 16px 4px;
    display: flex;
    flex-direction: column;
    gap: 16px;
  }

  &__row {
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(220px, 1fr));
    gap: 16px;
  }

  &__field {
    display: flex;
    flex-direction: column;
    gap: 6px;
  }

  &__label {
    font-size: 13px;
    font-weight: 500;
    color: #374151;
  }

  &__hint {
    font-size: 12px;
    color: #6b7280;
    line-height: 1.4;
    code {
      background: #f3f4f6;
      padding: 1px 4px;
      border-radius: 3px;
      font-family: monospace;
      font-size: 11px;
    }
  }

  &__divider {
    margin: 16px 0 4px;
    padding-bottom: 6px;
    border-bottom: 1px solid #e5e7eb;
    font-size: 14px;
    font-weight: 600;
    color: #1f2937;
  }

  &__actions {
    display: flex;
    gap: 12px;
    margin-top: 16px;
    padding-top: 16px;
    border-top: 1px solid #e5e7eb;
  }

  &__test-result {
    margin-top: 12px;
    h4 {
      margin: 0 0 6px;
      font-size: 13px;
      font-weight: 600;
    }
    pre {
      background: #1f2937;
      color: #d1d5db;
      padding: 12px;
      border-radius: 6px;
      font-size: 11px;
      max-height: 240px;
      overflow: auto;
    }
  }
}
</style>
